import React from "react";
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Button, Checkbox, Chip, Grid, Stack, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import FeatherIcon from "feather-icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TableComponent from "../../components/table/list.jsx";
import { CustomTooltip, LinearProgressWithLabel } from "../../consts.js";
import Utils from "../../utils";
import ChangeArchiveConfirm from "./change-archive-confirm.jsx";
import { DATE_FORMAT } from "../../helpers/constant.js";
import { format } from "date-fns";
import { useThemeMode } from "../../components/theme/index.jsx";
import { getDurationString, roundNumberHelper } from "../../helpers/index.js";

function CampaignArchived() {
    const [dataLoaded, setDataLoaded] = React.useState(false);
    const [openArchiveConfirm, setOpenArchiveConfirm] = React.useState(false);

    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [campaignConversationsId] = React.useState(params.get("id") || "");

    const [data, setData] = React.useState([]);
    const [spinner, setSpinner] = React.useState(false);
    const { colours, FONT } = useThemeMode();

    async function getCampaignData() {
        Utils.sendProgressBar(true);
        setSpinner(true);

        let list = []
        let nextToken = null;

        // api function
        const APIFunction = async () => {
            const response = await Utils.genericListMOD("listConversations", {
                filter: {
                    campaignConversationsId: { eq: campaignConversationsId },
                    archive: { eq: true },
                },
                nextToken
            }, true);
            list = list.concat(response.data.listConversations.items);
            setData(list);
            return response.data.listConversations.nextToken;
        }

        nextToken = await APIFunction();
        while (nextToken !== null) {
            nextToken = await APIFunction();
        }
        setSpinner(false);
    }

    React.useEffect(() => {
        if (!dataLoaded) {
            const fetchData = async () => {
                await getCampaignData();
                setDataLoaded(true);
            };
            fetchData();
        }
        window.scrollTo(0, 0);
    }, [dataLoaded]);

    /**
     * This method used to reload the page after api success
     */
    const handleUnarchiveSuccess = () => {
        setOpenArchiveConfirm(false);
        getCampaignData();
    }

    // define table headers
    const columns = [
        {
            label: (<Checkbox
                checked={
                    data.length === 0
                        ? false
                        : data
                            .map((q) => q.selected)
                            .filter((q) => q !== true).length === 0 || false
                }
                onClick={(e) => {
                    let d = Object.assign([], data);
                    d.forEach((q) => (q.selected = e.target.checked));
                    setData(d);
                }}
                inputProps={{ 'aria-label': 'Select all rows' }}
            />),
            key: "selected",
            sortable: false,
            renderItem: (row) => (<Checkbox
                checked={row.selected || false}
                onClick={(e) => {
                    e.stopPropagation();
                    const d = Object.assign([], data);
                    const index = d.findIndex((item) => item.id === row.id);
                    d[index].selected = e.target.checked;
                    setData(d);
                }}
                inputProps={{ 'aria-label': `Select ${row.file}` }}
            />)
        },
        {
            label: "File",
            key: "file",
            sortable: false,
            renderItem: (row) => (<Stack direction="row" alignItems="center" gap={2}>
                <FeatherIcon size={20} icon="phone-incoming" style={{ flexShrink: 0 }} />
                {row.file || "-"}
            </Stack>)
        },
        {
            label: "Agent",
            key: "agent",
            sortable: false,
            renderItem: (row) => row.agent || "-",
            style: { minWidth: 150 },
        },
        {
            label: "Call Date",
            key: "createdAt",
            sortable: true,
            renderItem: (row) => (<>
                {format(new Date(row.createdAt), DATE_FORMAT)}
            </>)
        },
        {
            label: "Avg. Call Duration",
            key: "duration",
            sortable: false,
            renderItem: (row) => (<>{getDurationString(row.duration, "-")}</>)
        },
        {
            label: "Avg. Vocalysd Score",
            key: "symblstatus",
            sortable: false,
            renderItem: (row) => (<>
                {row.symblstatus === "completed" ? (
                    <LinearProgressWithLabel variant="determinate" value={roundNumberHelper(row.VS)} />
                ) : row.symblstatus === "failed" || row.symblstatus === "DEEPGRAM_FAILED" ? (
                    <>
                        <CustomTooltip title={row.message || "Invalid or corrupt audio file"}>
                            <Typography component={"span"}>
                                <FeatherIcon
                                    aria-label="Invalid or corrupt audio file"
                                    icon="alert-triangle"
                                    size="20"
                                    style={{
                                        color: colours.orange100,
                                        flexShrink: 0
                                    }}
                                />
                            </Typography>
                        </CustomTooltip>
                    </>
                ) : (<Typography variant="inherit" display='flex' alignItems='center' gap={1} fontSize={FONT.SIZE_12}><CircularProgress aria-label="Loading status" size={14} color="info" /> Analysis in progress</Typography>)}
            </>)
        },
        {
            label: "# of Alert Hits",
            key: "alertcount",
            sortable: false,
            renderItem: (row) => (<>
                {row.alertcount > 0 ? (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ fontWeight: 500, color: colours.orange100 }}>
                        {row.alertcount}
                        <FeatherIcon
                            aria-label="Alert status"
                            icon="alert-circle"
                            size="20"
                            style={{ flexShrink: 0 }}
                        />
                    </Stack>
                ) : "-"}
            </>)
        }
    ]

    const headerActionButtons = () => {
        return (data.filter((z) => z.selected === true).length > 0 ? (
            <Grid
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                justifyContent="end"
                gap={2}
            >
                <Button
                    onClick={() => setOpenArchiveConfirm(true)}
                    variant="outlined"
                    startIcon={<UnarchiveIcon icon="archive" size={20} />}
                    endIcon={<Chip
                        size="small"
                        label={data.filter((z) => z.selected === true).length}
                        sx={{ px: .25 }}
                    />}
                    sx={{
                        '.MuiButton-endIcon .MuiChip-root': {
                            fontSize: FONT.SIZE_12,
                            fontWeight: 500
                        }
                    }}
                >
                    Unarchive
                </Button>
            </Grid>
        ) : null
        )
    }

    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            <Grid
                className="vocalysed-home-upadted campaigns"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <TableComponent
                    headerActionButtons={headerActionButtons}
                    spinner={spinner}
                    data={data}
                    columns={columns}
                    pagination={true}
                    noDataMessage={"No conversation found."}
                    searchKeys={["file", "agent"]}
                    defaultOptions={{ sortKey: 'createdAt', sortOrder: 'desc' }}
                    rowClick={(item) => {
                        if (item.symblstatus === "completed") {
                            navigate("/conversation?id=" + item.id);
                        }
                    }}
                />
            </Grid>

            {/* change to unarchive confirm popup */}
            <ChangeArchiveConfirm
                setArchiveState={false} // false to unarchive the conversation 
                open={openArchiveConfirm}
                data={data}
                onClose={() => setOpenArchiveConfirm(false)}
                onSuccess={handleUnarchiveSuccess} />
        </Grid>
    );
}

export default CampaignArchived;
